import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  EditOutlined,
  DeleteOutlined,
  Add as AddIcon,
} from "@mui/icons-material";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../shared/themes/redesignTokens";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  fetchGroups,
  createGroup,
  updateGroup,
  deleteGroup,
} from "../machineGroupsSlice";
import {
  MachineGroupViewModel,
  MachineGroupsState,
  ChangeMachineGroupViewModel,
} from "../models/groupModels";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import useTranslation from "../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";

// =====================================================================
//  GROUP TYPE OPTIONS
// =====================================================================

const GROUP_TYPE_OPTIONS = [
  { value: "location", labelKey: "groupTypeLocation" },
  { value: "line", labelKey: "groupTypeLine" },
  { value: "department", labelKey: "groupTypeDepartment" },
  { value: "type", labelKey: "groupTypeType" },
  { value: "custom", labelKey: "groupTypeCustom" },
];

// =====================================================================
//  COLOR PRESETS
// =====================================================================

const COLOR_PRESETS = [
  "#3498DB", // Blue
  "#2ECC71", // Green
  "#E74C3C", // Red
  "#F39C12", // Orange
  "#9B59B6", // Purple
  "#1ABC9C", // Teal
  "#E91E63", // Pink
  "#607D8B", // Blue Grey
];

// =====================================================================
//  PROPS
// =====================================================================

interface ManageGroupsDialogProps {
  open: boolean;
  onClose: () => void;
}

// =====================================================================
//  COMPONENT
// =====================================================================

const ManageGroupsDialog: React.FC<ManageGroupsDialogProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  const { groups, loading } = useAppSelector<MachineGroupsState>(
    (state: any) => state.machineGroups
  );

  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<MachineGroupViewModel | null>(null);
  const [formName, setFormName] = useState("");
  const [formGroupType, setFormGroupType] = useState("custom");
  const [formColor, setFormColor] = useState(COLOR_PRESETS[0]);
  const [formIcon, setFormIcon] = useState("");

  useEffect(() => {
    if (open) {
      dispatch(fetchGroups());
    }
  }, [open, dispatch]);

  // =====================================================================
  //  FORM HELPERS
  // =====================================================================

  const resetForm = () => {
    setFormName("");
    setFormGroupType("custom");
    setFormColor(COLOR_PRESETS[0]);
    setFormIcon("");
    setEditingGroup(null);
    setIsFormOpen(false);
  };

  const openCreateForm = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const openEditForm = (group: MachineGroupViewModel) => {
    setEditingGroup(group);
    setFormName(group.name);
    setFormGroupType(group.groupType);
    setFormColor(group.color || COLOR_PRESETS[0]);
    setFormIcon(group.icon || "");
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) return;

    const model: ChangeMachineGroupViewModel = {
      name: formName.trim(),
      groupType: formGroupType,
      color: formColor,
      icon: formIcon || null,
    };

    if (editingGroup) {
      await dispatch(updateGroup({ id: editingGroup.id, model }));
    } else {
      await dispatch(createGroup(model));
    }

    await dispatch(fetchGroups());
    resetForm();
  };

  const handleDelete = (group: MachineGroupViewModel) => {
    if (group.isDefault) return;

    confirm({
      title: translate("deleteGroup"),
      text: translate("deleteGroupConfirm", { name: group.name }),
      confirmButton: translate("yesDelete"),
      closeButton: tCommon("common.no"),
      severity: "error",
      onConfirm: async () => {
        await dispatch(deleteGroup(group.id));
        await dispatch(fetchGroups());
      },
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // =====================================================================
  //  RENDER
  // =====================================================================

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 700,
          fontSize: "18px",
          fontFamily: dt.font,
          color: dt.textPrimary,
          pb: 1,
        }}
      >
        {translate("manageGroups")}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        {/* Existing groups list */}
        {groups.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {groups.map((group) => (
              <Box
                key={group.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 12px",
                  borderRadius: dt.radiusSm,
                  border: `1px solid ${dt.cardBorder}`,
                  mb: 1,
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: dt.rowHover,
                    borderColor: dt.blue,
                  },
                }}
              >
                {/* Left: color dot + name + type badge + count */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  <Box
                    sx={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: group.color || dt.blue,
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: dt.textPrimary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {group.name}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "11px",
                      fontFamily: dt.fontMono,
                      color: dt.textMuted,
                      backgroundColor: dt.inputBg,
                      padding: "1px 8px",
                      borderRadius: "10px",
                      flexShrink: 0,
                    }}
                  >
                    {group.groupType}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "11px",
                      fontFamily: dt.fontMono,
                      fontWeight: 600,
                      color: dt.blue,
                      backgroundColor: dt.blueSoft,
                      padding: "1px 8px",
                      borderRadius: "10px",
                      flexShrink: 0,
                    }}
                  >
                    {group.machineCount}
                  </Box>
                </Box>

                {/* Right: edit/delete buttons */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
                  <Tooltip title={translate("editGroup")}>
                    <IconButton
                      size="small"
                      onClick={() => openEditForm(group)}
                      sx={{
                        color: dt.textSecondary,
                        "&:hover": { color: dt.blue },
                      }}
                    >
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Admin}>
                    <Tooltip
                      title={
                        group.isDefault
                          ? translate("cannotDeleteDefault")
                          : translate("deleteGroup")
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={group.isDefault}
                          onClick={() => handleDelete(group)}
                          sx={{
                            color: dt.textSecondary,
                            "&:hover": { color: "#E74C3C" },
                            "&.Mui-disabled": { opacity: 0.3 },
                          }}
                        >
                          <DeleteOutlined fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </PermissionGuard>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Inline create/edit form */}
        {isFormOpen && (
          <Box
            sx={{
              border: `1px solid ${dt.cardBorder}`,
              borderRadius: dt.radiusSm,
              padding: "16px",
              mb: 2,
              backgroundColor: dt.headerBg,
            }}
          >
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: dt.textPrimary,
                mb: 2,
              }}
            >
              {editingGroup ? translate("editGroup") : translate("createGroup")}
            </Box>

            {/* Name */}
            <TextField
              label={translate("groupName")}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              autoFocus
            />

            {/* Group Type */}
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>{translate("groupType")}</InputLabel>
              <Select
                value={formGroupType}
                label={translate("groupType")}
                onChange={(e) => setFormGroupType(e.target.value)}
              >
                {GROUP_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {translate(opt.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Color picker (preset palette) */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: dt.textSecondary,
                  mb: 1,
                }}
              >
                {translate("groupColor")}
              </Box>
              <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {COLOR_PRESETS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormColor(color)}
                    sx={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: color,
                      cursor: "pointer",
                      border:
                        formColor === color
                          ? "3px solid #1a1a2e"
                          : "2px solid transparent",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "scale(1.15)",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Icon (optional text field) */}
            <TextField
              label={translate("groupIcon")}
              value={formIcon}
              onChange={(e) => setFormIcon(e.target.value)}
              size="small"
              fullWidth
              sx={{ mb: 2 }}
              placeholder="e.g. factory, location_on"
            />

            {/* Form actions */}
            <Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <Button
                onClick={resetForm}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  color: dt.textSecondary,
                }}
              >
                {tCommon("buttons.cancel")}
              </Button>
              <Button
                variant="contained"
                disabled={!formName.trim() || loading}
                onClick={handleSave}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: dt.radiusSm,
                  background: accentGradientDark,
                  boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                  "&:hover": {
                    background: accentGradientDarkHover,
                  },
                }}
              >
                {editingGroup ? tCommon("buttons.save") : translate("createGroup")}
              </Button>
            </Box>
          </Box>
        )}

        {/* Add Group button (when form is closed) */}
        {!isFormOpen && (
          <Button
            startIcon={<AddIcon />}
            onClick={openCreateForm}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              color: dt.blue,
              "&:hover": {
                backgroundColor: dt.blueSoft,
              },
            }}
          >
            {translate("createGroup")}
          </Button>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: dt.radiusSm,
            borderColor: dt.cardBorder,
            color: dt.textSecondary,
            "&:hover": {
              borderColor: dt.blue,
              color: dt.blue,
            },
          }}
        >
          {tCommon("buttons.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageGroupsDialog;
